// Lightweight self-hosted analytics client.
//
// Exports:
//   useTracking({ page, locale, demoMode })   – fires a page_view on every change
//   trackEvent(name, { locale, demoMode })    – ad-hoc events
//   updateSessionFlag({ key, value })         – mark reachedCpr / called112
//
// On unload / tab hide the session duration is POSTed via sendBeacon.

import { useEffect, useRef } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const VID_KEY = "sa_visitor_id";

function makeId() {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
    return (
        Math.random().toString(36).slice(2) +
        Date.now().toString(36) +
        Math.random().toString(36).slice(2)
    );
}

function getVisitorId() {
    try {
        let id = localStorage.getItem(VID_KEY);
        if (!id) {
            id = makeId();
            localStorage.setItem(VID_KEY, id);
        }
        return id;
    } catch {
        return "anon-" + makeId();
    }
}

function detectCountry() {
    try {
        const tag =
            (navigator.languages && navigator.languages[0]) ||
            navigator.language ||
            "";
        // examples: "de-CH", "en-US", "de" -> de => unknown
        const parts = tag.split(/[-_]/);
        if (parts.length >= 2) {
            const candidate = parts[1].toUpperCase().replace(/[^A-Z]/g, "");
            if (candidate.length === 2) return candidate;
        }
        return "unknown";
    } catch {
        return "unknown";
    }
}

// Single session per page load (or per ~30 min of inactivity – simplified).
const sessionState = {
    sessionId: makeId(),
    startedAt: Date.now(),
    maxStep: "landing",
    reachedCpr: false,
    called112: false,
    locale: null,
    country: detectCountry(),
    demoMode: false,
};

function postSession() {
    if (!API) return;
    const body = JSON.stringify({
        visitorId: getVisitorId(),
        sessionId: sessionState.sessionId,
        durationMs: Date.now() - sessionState.startedAt,
        locale: sessionState.locale || "unknown",
        country: sessionState.country,
        demoMode: !!sessionState.demoMode,
        maxStep: sessionState.maxStep,
        reachedCpr: sessionState.reachedCpr,
        called112: sessionState.called112,
    });
    try {
        const url = `${API}/api/stats/session`;
        const blob = new Blob([body], { type: "application/json" });
        if (navigator.sendBeacon && navigator.sendBeacon(url, blob)) return;
        // fallback
        fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body,
            keepalive: true,
        }).catch(() => {});
    } catch {
        /* silent */
    }
}

let sessionListenersInstalled = false;
function installSessionListeners() {
    if (sessionListenersInstalled || typeof window === "undefined") return;
    sessionListenersInstalled = true;
    // Persist every 15s as heartbeat (so we still get a duration even if
    // the browser is killed without firing visibilitychange).
    setInterval(postSession, 15_000);
    document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") postSession();
    });
    window.addEventListener("pagehide", postSession);
}

const STEP_RANK = {
    landing: 0,
    safety: 1,
    triage: 2,
    cpr: 3,
    aed: 1, // aed is a side-branch, don't override cpr
};

export function trackEvent(eventType, opts = {}) {
    if (!API) return;
    const locale = opts.locale || sessionState.locale || "unknown";
    const demoMode =
        typeof opts.demoMode === "boolean" ? opts.demoMode : sessionState.demoMode;
    fetch(`${API}/api/stats/track`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            visitorId: getVisitorId(),
            sessionId: sessionState.sessionId,
            page: opts.page || sessionState.maxStep,
            locale,
            country: sessionState.country,
            demoMode: !!demoMode,
            eventType,
            referrer:
                typeof document !== "undefined" ? document.referrer || null : null,
        }),
    }).catch(() => {});
}

export function updateSessionFlag({ key, value }) {
    if (key === "reachedCpr") sessionState.reachedCpr = !!value;
    if (key === "called112") sessionState.called112 = !!value;
}

export function useTracking({ page, locale, demoMode }) {
    const lastKey = useRef("");
    useEffect(() => {
        if (!API) return;
        installSessionListeners();
        // sync session state for future events / session post
        sessionState.locale = locale || sessionState.locale;
        sessionState.demoMode = !!demoMode;
        const rank = STEP_RANK[page];
        const curRank = STEP_RANK[sessionState.maxStep] ?? 0;
        if (typeof rank === "number" && rank > curRank) {
            sessionState.maxStep = page;
        }
        if (page === "cpr") sessionState.reachedCpr = true;

        const key = `${page}|${locale}|${demoMode ? 1 : 0}`;
        if (lastKey.current === key) return;
        lastKey.current = key;
        trackEvent("page_view", { page, locale, demoMode });
    }, [page, locale, demoMode]);
}
